'use client';

import { useBranches } from "@/hooks/use-branches-locations";
import { calculateDistanceAndTime, calculateRoadDistance, DistanceResult } from "@/lib/calculate-distance-and-time";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { MapPin, Clock, Star, ChevronRight, Navigation, RefreshCcw } from "lucide-react";
import { getBranchDisplayHours, isBranchOpen } from "@/lib/branch-hours";
import { useServerTime } from "@/utils/server-time";

type LatLng = {
  lat: number;
  lon: number;
};

type onBranchClickType = (params: {
  id: string;
  name: string;
  subtitle: string;
}) => void;

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 24 } }
} as const;

export default function BranchList({ onBranchClick }: { onBranchClick: onBranchClickType }) {
  const [userLocation, setUserLocation] = useState<LatLng | null>(null);
  const [roadDistances, setRoadDistances] = useState<Record<string, DistanceResult>>({});
  const { t } = useTranslation();
  const { serverDate } = useServerTime();
  const currentTime = serverDate;

  // Get current location
  useEffect(() => {
    if (!("geolocation" in navigator)) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation({
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
        });
      },
      (err) => {
        console.error("Geolocation error:", err);
      }
    );
  }, []);

  const { data: branches, isLoading, error, refetch } = useBranches(userLocation);

  useEffect(() => {
    if (!userLocation || !branches) return;

    const fetchDistances = async () => {
      for (const branch of branches) {
        if (!branch.location.coordinates?.coordinates) continue;

        // Skip if we already have it
        if (roadDistances[branch.id]) continue;

        const result = await calculateRoadDistance(
          { lat: userLocation.lat, lng: userLocation.lon },
          {
            lat: branch.location.coordinates.coordinates[1],
            lng: branch.location.coordinates.coordinates[0],
          }
        );

        if (result) {
          setRoadDistances((prev) => ({ ...prev, [branch.id]: result }));
        }
      }
    };

    fetchDistances();
  }, [userLocation, branches]);

  if (isLoading) {
    return (
      <div className="px-4 py-3 space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-50 mb-3">
          <MapPin className="w-6 h-6 text-red-500" />
        </div>
        <p className="text-gray-900 font-medium">{t("Failed to load branches")}</p>
 <button 
            onClick={() => refetch()} 
            className="inline-flex flex items-center gap-2 px-5 py-2 bg-pink-600 text-white rounded-full shadow-xl shadow-pink-200 hover:shadow-2xl hover:shadow-pink-300 hover:bg-pink-700 hover:-translate-y-1 transition-all duration-300 active:scale-95 mt-3"
          >
            <RefreshCcw size={16} />
            {t("retry")}
          </button>      </div>
    );
  }

  if (!branches || branches.length === 0) {
    return (
      <div className="p-8 text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-50 mb-3">
          <MapPin className="w-6 h-6 text-gray-400" />
        </div>
        <p className="text-gray-500">{t("No branches found nearby")}</p>
      </div>
    );
  }

  return (
    <motion.div 
      variants={containerVariants}
      initial="hidden"
      animate="show"
      className="px-4 py-3 space-y-3 pb-6 md:pb-4"
    >
      {branches.map((branch) => {
        const straightLine = calculateDistanceAndTime(
          { lat: userLocation?.lat ?? 0, lng: userLocation?.lon ?? 0 },
          {
            lat: branch.location.coordinates?.coordinates[1],
            lng: branch.location.coordinates?.coordinates[0],
          },
          branch.name,
        );

        const { kilometers, minutes } = roadDistances[branch.id] || straightLine;

        const isSaturday = currentTime.getDay() === 6;
        const isOpen = isBranchOpen(branch.name, currentTime);

        return (
          <motion.div
            key={branch.id}
            variants={itemVariants}
            whileHover={{ scale: 1.02, backgroundColor: "rgba(249, 250, 251, 1)" }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onBranchClick({
              id: branch.id, 
              name: branch.name, 
              subtitle: branch.location?.name ? String(branch.location.name) : "",
            })}
            className="group relative flex items-start gap-3 p-3 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all cursor-pointer overflow-hidden"
          >
            {/* Branch Icon */}
            <div className="flex-shrink-0 w-10 h-10 bg-pink-50 rounded-full flex items-center justify-center">
               <img src="/icon/branch.svg" alt="branch" width={20} height={20} className="w-5 h-5" />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h4 className="font-bold text-sm md:text-base text-gray-900 leading-tight mb-0.5 truncate">{t(branch.name)}</h4>
                  <p className="text-xs text-gray-500 truncate">{t(String(branch.location.name))}</p>
                </div>
                {/* Rating */}
               
              </div>

              {/* Meta Info */}
              <div className="flex items-center gap-x-3 gap-y-2 mt-2.5 flex-wrap">
                <div className={`flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full ${isOpen ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
                  <div className={`w-1.5 h-1.5 rounded-full ${isOpen ? 'bg-green-500' : 'bg-red-500'}`} />
                  {isOpen ? t("Open") : t("Closed")}
                </div>
                
                <div className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap">
                  <Clock className="w-3.5 h-3.5" />
                  <span>{getBranchDisplayHours(branch.name, isSaturday)}</span>
                </div>

                {userLocation && (
                  <div className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full md:ml-auto whitespace-nowrap">
                    <Navigation className="w-3 h-3" />
                    <span>{kilometers} km</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Hover Arrow */}
            <div className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity -mr-2 group-hover:mr-0 hidden md:block">
               <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>
          </motion.div>
        );
      })}
    </motion.div>
  );
}
